import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  Renderer2,
} from '@angular/core';

import { UaParserService } from '@hglobal/fin-ui';

export enum SwipeDirectionEnum {
  Up = 'up',
  Down = 'down',
  Left = 'left',
  Right = 'right',
}

export type SwipeDirection =
  | SwipeDirectionEnum.Up
  | SwipeDirectionEnum.Down
  | SwipeDirectionEnum.Left
  | SwipeDirectionEnum.Right;

@Directive({
  selector: '[iwSwipeGesture]',
})
export class IwSwipeGestureDirective {
  #startX = 0;
  #startY = 0;
  #currentDx = 0;
  #currentDy = 0;
  #isSwiping = false;
  #lockedAxis: 'x' | 'y' | null = null;

  #animationEasing = 'cubic-bezier(0.22, 1, 0.36, 1)';

  readonly #el = inject(ElementRef);
  readonly #renderer = inject(Renderer2);
  readonly #uaParser = inject(UaParserService);

  swipeThreshold = input(100);
  animationDuration = input(300);
  allowedDirections = input([SwipeDirectionEnum.Down]);

  swipe = output<SwipeDirection>();
  swipeUp = output<void>();
  swipeDown = output<void>();
  swipeLeft = output<void>();
  swipeRight = output<void>();
  pan = output<{ dx: number; dy: number }>();

  constructor() {
    const el = this.#el.nativeElement;
    this.#renderer.setStyle(el, 'touch-action', 'none');
    this.#renderer.setStyle(el, 'will-change', 'transform');
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent) {
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#isSwiping = true;
    this.#lockedAxis = null;
    this.#setTransition('none');
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (!this.#isSwiping || !this.#uaParser.isMobile) return;

    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;

    this.#lockAxis(dx, dy);
    const direction = this.#calculateDirection(dx, dy);
    this.#updateTranslation(direction, dx, dy);

    this.pan.emit({ dx: this.#currentDx, dy: this.#currentDy });
  }

  @HostListener('pointerup')
  onPointerUp() {
    if (!this.#isSwiping) return;
    this.#isSwiping = false;

    const direction = this.#calculateDirection(
      this.#currentDx,
      this.#currentDy,
    );

    if (!this.#isSwipeValid(direction)) {
      this.#animateBack();
      return;
    }

    this.#emitSwipeEvents(direction);
    this.#animateOut(direction);
  }

  @HostListener('pointercancel')
  @HostListener('lostpointercapture')
  onPointerCancel() {
    this.#isSwiping = false;
    this.#animateBack();
  }

  #lockAxis(dx: number, dy: number) {
    if (!this.#lockedAxis) {
      this.#lockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
  }

  #calculateDirection(dx: number, dy: number): SwipeDirection {
    if (this.#lockedAxis === 'x') {
      return dx > 0 ? SwipeDirectionEnum.Right : SwipeDirectionEnum.Left;
    } else {
      return dy > 0 ? SwipeDirectionEnum.Down : SwipeDirectionEnum.Up;
    }
  }

  #isSwipeValid(direction: SwipeDirection): boolean {
    const maxDelta = Math.max(
      Math.abs(this.#currentDx),
      Math.abs(this.#currentDy),
    );
    return (
      maxDelta >= this.swipeThreshold() &&
      this.allowedDirections().includes(direction)
    );
  }

  #updateTranslation(direction: SwipeDirection, dx: number, dy: number) {
    if (!this.allowedDirections().includes(direction)) {
      this.#currentDx = 0;
      this.#currentDy = 0;
    } else {
      this.#currentDx = this.#lockedAxis === 'x' ? dx : 0;
      this.#currentDy = this.#lockedAxis === 'y' ? dy : 0;
    }
    this.#updateTransform(this.#currentDx, this.#currentDy);
  }

  #updateTransform(dx: number, dy: number) {
    requestAnimationFrame(() => {
      this.#renderer.setStyle(
        this.#el.nativeElement,
        'transform',
        `translate(${dx}px, ${dy}px)`,
      );
    });
  }

  #emitSwipeEvents(direction: SwipeDirection) {
    this.swipe.emit(direction);
    switch (direction) {
      case SwipeDirectionEnum.Up:
        this.swipeUp.emit();
        break;
      case SwipeDirectionEnum.Down:
        this.swipeDown.emit();
        break;
      case SwipeDirectionEnum.Left:
        this.swipeLeft.emit();
        break;
      case SwipeDirectionEnum.Right:
        this.swipeRight.emit();
        break;
    }
  }

  #setTransform(x: number, y: number) {
    this.#renderer.setStyle(
      this.#el.nativeElement,
      'transform',
      `translate(${x}px, ${y}px)`,
    );
  }

  #setTransition(value: string) {
    this.#renderer.setStyle(this.#el.nativeElement, 'transition', value);
  }

  #animateBack() {
    const startX = this.#currentDx;
    const startY = this.#currentDy;
    const duration = this.animationDuration();
    const startTime = performance.now();

    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      const x = startX * (1 - eased);
      const y = startY * (1 - eased);

      this.#setTransform(x, y);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.#currentDx = 0;
        this.#currentDy = 0;
      }
    };

    requestAnimationFrame(animate);
  }

  #animateOut(direction: SwipeDirection) {
    const { x, y } = this.#getFinalTranslation(direction);
    this.#setTransition(
      `transform ${this.animationDuration}ms ${this.#animationEasing}`,
    );
    requestAnimationFrame(() => this.#setTransform(x, y));
  }

  #getFinalTranslation(direction: SwipeDirection) {
    const outDistance = Math.max(window.innerWidth, window.innerHeight);
    switch (direction) {
      case SwipeDirectionEnum.Up:
        return { x: 0, y: -outDistance };
      case SwipeDirectionEnum.Down:
        return { x: 0, y: outDistance };
      case SwipeDirectionEnum.Left:
        return { x: -outDistance, y: 0 };
      case SwipeDirectionEnum.Right:
        return { x: outDistance, y: 0 };
    }
  }
}
